import { userIdentityChanged } from 'auth/identityActions';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createAction, createSlice } from '@reduxjs/toolkit';
import { sortStorageUnits as sortStorageUnitsFn } from 'util/storageUnitSort';
import type {
  StorageUnitsReducerInterface,
  StorageUnit,
  KeycloakUser,
  AccessRead,
  Sort,
  Lease,
  FileUpload,
  CurrentFile,
  AdvancedFilters,
  ProjectMetadata,
  WorkflowMetadata,
} from './types';
import {
  SortType,
  Filter,
  StorageUnitType,
  StorageUnitStatus,
  RequestStatus,
  SortDirection,
  LeaseAction,
  LeaseType,
  FileUploadStatus,
  LEASE_ON_STORAGE_UNIT_SUCCESS,
} from './types';
import { addFileEndingIfMissing } from 'util/file';
import { normalizeLeases } from './utils';

const normalizeLeaseFields = (unit: StorageUnit): StorageUnit =>
  normalizeLeases(unit);

const ensureSoftLeases = (unit?: StorageUnit) => {
  if (!unit) {
    return;
  }
  const normalized = normalizeLeases(unit);
  unit.exclusiveLease = normalized.exclusiveLease;
  unit.softLeases = normalized.softLeases;
};

const normalizeFilters = (filters: Filter[]) => {
  const exclusivePairs = [
    [Filter.DRAFT, Filter.APPROVED],
    [Filter.LOCKED, Filter.UNLOCKED],
    [Filter.DOWNLOADED, Filter.CLOUD],
    [Filter.SHARED, Filter.PRIVATE],
  ];
  return [
    ...new Set(
      filters.filter((filter, index) =>
        exclusivePairs.every(
          (pair) =>
            !pair.includes(filter) ||
            !pair.some(
              (other) => other !== filter && filters.lastIndexOf(other) > index,
            ),
        ),
      ),
    ),
  ];
};

const leaseOnStorageUnitSuccess = createAction<{
  id: string;
  type: LeaseAction;
  leaseType?: LeaseType | null;
  lease?: Lease;
  userId?: string;
}>(LEASE_ON_STORAGE_UNIT_SUCCESS);

const initialState: StorageUnitsReducerInterface = {
  approvedFiles: [],
  recentFiles: [],
  templates: [],
  storageUnits: [],
  archived: [],
  folderPath: [],
  resolvedStorageUnitIds: {},
  currentFile: {
    id: '',
    name: '',
    status: [],
    updatedAt: undefined,
    isSynced: true,
    syncedAt: null,
    exclusiveLease: undefined,
    softLeases: [],
    access: {
      direct: [],
      indirect: [],
    },
  },
  loading: false,
  directAccess: false,
  shareModal: {
    accessInformation: undefined,
    loading: false,
  },
  searchString: '',
  members: {
    accessInformation: undefined,
    loading: false,
  },
  moreDataToRetrieve: false,
  fetchedCount: 0,
  filters: [],
  advancedFilters: {},
  sort: {
    type: SortType.LAST_MODIFIED,
    direction: SortDirection.ASC,
  },
  requestStatus: RequestStatus.IDLE,
  currentUploads: [],
};

type GetStorageUnitsResponsePayload = {
  storageUnits?: StorageUnit[];
  folderPath?: StorageUnit[];
  sort?: Sort;
  replace?: boolean;
  fetchedCount?: number;
};

type CreateStorageUnitRequestPayload = {
  tempId: string;
  name: string;
  parentId?: string;
  fileData?: string;
  status?: StorageUnitStatus[];
  isUpload?: boolean;
  projectMetadata?: ProjectMetadata;
  flowsheetMetadata?: WorkflowMetadata;
  ownerUserId?: string | null;
};

type CreateStorageUnitSuccessPayload = {
  tempId: string;
  storageUnit: StorageUnit;
};

const storageUnitsSlice = createSlice({
  name: 'storageUnitsState',
  initialState,
  reducers: {
    getStorageUnitsResponse(
      state,
      action: PayloadAction<GetStorageUnitsResponsePayload>,
    ) {
      const { storageUnits, folderPath, replace, fetchedCount } =
        action.payload;
      const shouldReplace = replace === undefined || replace;

      if (storageUnits) {
        if (shouldReplace) {
          state.storageUnits = storageUnits.map(normalizeLeaseFields);
        } else {
          state.storageUnits = [
            ...state.storageUnits,
            ...storageUnits.map(normalizeLeaseFields),
          ];
        }
      }
      state.folderPath = folderPath ?? state.folderPath;
      if (fetchedCount !== undefined) {
        state.fetchedCount = fetchedCount;
      }
    },
    createStorageUnitRequested(
      state,
      action: PayloadAction<CreateStorageUnitRequestPayload>,
    ) {
      const {
        tempId,
        name,
        fileData,
        parentId,
        status,
        isUpload,
        projectMetadata,
        flowsheetMetadata,
        ownerUserId,
      } = action.payload;
      const type = fileData ? StorageUnitType.File : StorageUnitType.Folder;
      const access = {
        direct: ownerUserId ? [ownerUserId] : [],
        indirect: [],
      };
      const now = new Date();
      const newStorageUnit = {
        name:
          type === StorageUnitType.File ? addFileEndingIfMissing(name) : name,
        parentId,
        type,
        fileData,
        status: status ?? [],
        ...(projectMetadata ? { project: projectMetadata } : {}),
        ...(flowsheetMetadata ? { flowsheetMetadata } : {}),
        exclusiveLease: undefined,
        softLeases: [],
      };

      const currentFolderId = state.folderPath[state.folderPath.length - 1]?.id;
      const belongsInCurrentFolder = parentId === currentFolderId;

      if (belongsInCurrentFolder) {
        state.storageUnits = [
          {
            ...newStorageUnit,
            id: tempId,
            createdAt: now,
            updatedAt: now,
            status: status ?? [],
            exclusiveLease: undefined,
            softLeases: [],
            access,
            name:
              type === StorageUnitType.File
                ? addFileEndingIfMissing(name)
                : name,
            isSynced: false,
            syncedAt: null,
            localUpdatedAt: now.toISOString(),
            pendingAction: 'create',
          },
          ...state.storageUnits,
        ];
      }
      if (isUpload) {
        state.currentUploads.push({
          id: tempId,
          name: newStorageUnit.name,
          status: FileUploadStatus.UPLOADING,
        });
      }
    },
    createStorageUnitSuccess(
      state,
      action: PayloadAction<CreateStorageUnitSuccessPayload>,
    ) {
      const { tempId, storageUnit } = action.payload;
      const normalizedStorageUnit = normalizeLeaseFields(storageUnit);
      state.storageUnits = state.storageUnits.map((su) => {
        if (su.id === tempId) {
          return normalizedStorageUnit;
        }
        if (su.parentId === tempId) {
          return {
            ...su,
            parentId: normalizedStorageUnit.id,
          };
        }
        return su;
      });
      state.folderPath = state.folderPath.map((su) => {
        if (su.id === tempId) {
          return normalizedStorageUnit;
        }
        if (su.parentId === tempId) {
          return {
            ...su,
            parentId: normalizedStorageUnit.id,
          };
        }
        return su;
      });
      if (tempId !== normalizedStorageUnit.id) {
        state.resolvedStorageUnitIds[tempId] = normalizedStorageUnit.id;
      }
      const upload = state.currentUploads.find((u) => u.id === tempId);

      if (upload) {
        upload.status = FileUploadStatus.SUCCEEDED;
        upload.id = normalizedStorageUnit.id;
      }
    },
    addStorageUnit(state, action: PayloadAction<StorageUnit>) {
      state.storageUnits.unshift(normalizeLeaseFields(action.payload));
    },
    setExplorerLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    removeStorageUnitSuccess(state, action: PayloadAction<string>) {
      const unit = [...state.storageUnits, ...state.templates].find(
        (u) => u.id === action.payload,
      );
      if (unit?.status.includes(StorageUnitStatus.TEMPLATE)) {
        state.templates = state.templates.filter(
          (su: StorageUnit) => su.id !== action.payload,
        );
      } else {
        state.archived = state.archived.filter(
          (su: StorageUnit) => su.id !== action.payload,
        );
        state.storageUnits = state.storageUnits.filter(
          (su: StorageUnit) => su.id !== action.payload,
        );
      }
    },
    getAccessInformationForStorageUnitFromContextMenuSuccess(
      state,
      action: PayloadAction<AccessRead>,
    ) {
      state.shareModal.accessInformation = action.payload;
    },
    getAccessInformationForStorageUnitFromMembersSuccess(
      state,
      action: PayloadAction<AccessRead>,
    ) {
      state.members.accessInformation = action.payload;
    },
    setSearchString(state, action: PayloadAction<string>) {
      state.searchString = action.payload;
      state.fetchedCount = 0;
      state.moreDataToRetrieve = false;
    },
    setSharedWithUsersLoading(state, action: PayloadAction<boolean>) {
      state.shareModal.loading = action.payload;
    },
    grantAccessFromContextMenuSuccess(
      state,
      action: PayloadAction<{ users: KeycloakUser[]; storageUnitId: string }>,
    ) {
      const { storageUnitId, users } = action.payload;

      if (state.shareModal.accessInformation) {
        state.shareModal.accessInformation.direct = [
          ...state.shareModal.accessInformation.direct,
          ...users,
        ];
      }
      const unit = state.storageUnits.find((u) => u.id === storageUnitId);
      if (unit) {
        unit.access.direct = [...unit.access.direct, ...users.map((u) => u.id)];
      }
    },
    grantAccessFromMembersSuccess(
      state,
      action: PayloadAction<KeycloakUser[]>,
    ) {
      const users = action.payload;
      if (state.members.accessInformation) {
        state.members.accessInformation.direct = [
          ...state.members.accessInformation.direct,
          ...users,
        ];
      }
    },
    clearAccessInformationForStorageUnit(state) {
      state.shareModal.accessInformation = undefined;
    },
    removeAccessFromContextMenuSuccess(
      state,
      action: PayloadAction<{
        userId: string;
        email: string;
        storageUnitId: string;
      }>,
    ) {
      const { email, userId, storageUnitId } = action.payload;
      if (state.shareModal.accessInformation) {
        state.shareModal.accessInformation.direct =
          state.shareModal.accessInformation.direct.filter(
            (user: KeycloakUser) => user.email !== email,
          );
      }
      const unit = state.storageUnits.find((u) => u.id === storageUnitId);

      if (unit) {
        unit.access.direct = unit.access.direct.filter((u) => u !== userId);
      }
    },
    removeAccessFromMembersSuccess(state, action: PayloadAction<string>) {
      if (state.members.accessInformation) {
        state.members.accessInformation.direct =
          state.members.accessInformation.direct.filter(
            (user: KeycloakUser) => user.email !== action.payload,
          );
      }
    },
    createStorageUnitFailure(state, action: PayloadAction<string>) {
      state.storageUnits = state.storageUnits.filter(
        (s) => s.id !== action.payload,
      );
      const failedUpload = state.currentUploads.find(
        (u) => u.id === action.payload,
      );

      if (failedUpload) {
        failedUpload.status = FileUploadStatus.FAILED;
        failedUpload.errorText = 'Connection error';
      }
    },
    updateStorageUnitSuccess(state, action: PayloadAction<StorageUnit>) {
      const updated = normalizeLeaseFields(action.payload);
      if (updated.status.includes(StorageUnitStatus.TEMPLATE)) {
        state.templates = [
          updated,
          ...state.templates.filter((s) => s.id !== updated.id),
        ];
      } else {
        state.storageUnits = [
          updated,
          ...state.storageUnits.filter((s) => s.id !== updated.id),
        ];
      }
    },
    updateStorageUnitLeases(
      state,
      action: PayloadAction<{
        id: string;
        exclusiveLease?: Lease | null;
        softLeases?: Lease[];
      }>,
    ) {
      const { id, exclusiveLease, softLeases } = action.payload;
      const normalizedExclusive = exclusiveLease ?? undefined;
      const normalizedSoftLeases = (softLeases ?? []).map((lease) => ({
        ...lease,
      }));

      const apply = (unit?: StorageUnit) => {
        if (!unit) {
          return;
        }
        unit.exclusiveLease = normalizedExclusive;
        unit.softLeases = normalizedSoftLeases.map((lease) => ({ ...lease }));
      };

      [
        state.storageUnits,
        state.templates,
        state.approvedFiles,
        state.archived,
      ].forEach((collection) => apply(collection.find((u) => u.id === id)));

      if (state.currentFile.id === id) {
        state.currentFile = {
          ...state.currentFile,
          exclusiveLease: normalizedExclusive,
          softLeases: normalizedSoftLeases.map((lease) => ({ ...lease })),
        };
      }
    },
    clearMembers(state) {
      state.members.accessInformation = undefined;
    },
    setMembersLoading(state, action: PayloadAction<boolean>) {
      state.members.loading = action.payload;
    },
    setMoreDataToRetrieve(state, action: PayloadAction<boolean>) {
      state.moreDataToRetrieve = action.payload;
    },
    setDirectAccess(state, action: PayloadAction<boolean>) {
      state.directAccess = action.payload;
    },
    sortStorageUnits(state, action: PayloadAction<Sort>) {
      state.storageUnits = sortStorageUnitsFn(
        state.storageUnits.map(normalizeLeaseFields),
        action.payload,
      );
      state.sort = action.payload;
    },
    setSort(state, action: PayloadAction<Sort>) {
      state.sort = action.payload;
    },
    setTemplates(
      state,
      action: PayloadAction<{ templates: StorageUnit[]; replace: boolean }>,
    ) {
      const { replace, templates } = action.payload;
      if (replace) {
        state.templates = templates.map(normalizeLeaseFields);
      } else {
        state.templates = [
          ...state.templates,
          ...templates.map(normalizeLeaseFields),
        ];
      }
      state.folderPath = [];
    },
    setArchived(
      state,
      action: PayloadAction<{ units: StorageUnit[]; replace: boolean }>,
    ) {
      const { replace, units } = action.payload;
      if (replace) {
        state.archived = units.map(normalizeLeaseFields);
      } else {
        state.archived = [
          ...state.archived,
          ...units.map(normalizeLeaseFields),
        ];
      }
      state.folderPath = [];
    },
    setApprovedFiles(
      state,
      action: PayloadAction<{ files: StorageUnit[]; replace: boolean }>,
    ) {
      const { files, replace } = action.payload;
      if (replace) {
        state.approvedFiles = files.map(normalizeLeaseFields);
      } else {
        state.approvedFiles = [
          ...state.approvedFiles,
          ...files.map(normalizeLeaseFields),
        ];
      }
      state.folderPath = [];
    },
    setRecentFiles(state, action: PayloadAction<StorageUnit[]>) {
      state.recentFiles = action.payload.map(normalizeLeaseFields);
    },
    setFilters(state, action: PayloadAction<Filter[]>) {
      state.filters = normalizeFilters(action.payload);
      state.fetchedCount = 0;
      state.moreDataToRetrieve = false;
    },
    setFilterSelection(
      state,
      action: PayloadAction<{
        filters: Filter[];
        advancedFilters: AdvancedFilters;
      }>,
    ) {
      state.filters = normalizeFilters(action.payload.filters);
      state.advancedFilters = action.payload.advancedFilters;
      state.fetchedCount = 0;
      state.moreDataToRetrieve = false;
    },
    setAdvancedFilters(state, action: PayloadAction<AdvancedFilters>) {
      state.advancedFilters = action.payload;
      state.fetchedCount = 0;
      state.moreDataToRetrieve = false;
    },
    setRequestStatus(state, action: PayloadAction<RequestStatus>) {
      state.requestStatus = action.payload;
    },
    addUpload(state, action: PayloadAction<FileUpload>) {
      state.currentUploads.push(action.payload);
    },
    setCurrentUploads(state, action: PayloadAction<FileUpload[]>) {
      state.currentUploads = action.payload;
    },
    setCurrentFile(state, action: PayloadAction<CurrentFile>) {
      state.currentFile = normalizeLeases(action.payload as StorageUnit);
    },
    setCurrentFileName(state, action: PayloadAction<string>) {
      state.currentFile.name = action.payload;
    },
    archiveStorageUnitSuccess(state, action: PayloadAction<string>) {
      state.storageUnits = state.storageUnits.filter(
        (u) => u.id !== action.payload,
      );
      state.templates = state.templates.filter((u) => u.id !== action.payload);
    },
    restoreStorageUnitSuccess(state, action: PayloadAction<StorageUnit>) {
      state.archived = state.archived.filter((u) => u.id !== action.payload.id);
      state.storageUnits.unshift(normalizeLeaseFields(action.payload));
    },
    deleteAllArchviedSuccess(state) {
      state.archived = [];
    },
    generateMissingThumbnail(_state, _action: PayloadAction<string>) {},
  },
  extraReducers: (builder) => {
    builder.addCase(userIdentityChanged, () => initialState);
    builder.addCase(leaseOnStorageUnitSuccess, (state, action) => {
      const { id, type, leaseType, lease, userId } = action.payload;
      const leaseCopy = lease ? { ...lease } : undefined;
      const releaseUserId = userId ?? leaseCopy?.userId;
      const rawTypeFromLease = leaseCopy?.leaseType;
      const rawType = rawTypeFromLease ?? leaseType;
      const effectiveType =
        rawType ?? (leaseCopy ? LeaseType.Exclusive : undefined);

      const applyLeaseChanges = (unit?: StorageUnit) => {
        if (!unit) {
          return;
        }
        ensureSoftLeases(unit);

        if (type === LeaseAction.Acquire) {
          if (effectiveType === LeaseType.Exclusive) {
            unit.exclusiveLease = leaseCopy ?? undefined;
            unit.softLeases = [];
          } else if (effectiveType === LeaseType.Soft && leaseCopy) {
            unit.softLeases = [
              ...unit.softLeases.filter(
                (existing) => existing.userId !== leaseCopy.userId,
              ),
              leaseCopy,
            ];
          }
        } else if (type === LeaseAction.Release) {
          if (!leaseType || effectiveType === LeaseType.Exclusive) {
            if (
              releaseUserId &&
              unit.exclusiveLease?.userId === releaseUserId
            ) {
              unit.exclusiveLease = undefined;
            } else if (!releaseUserId) {
              unit.exclusiveLease = undefined;
            }
          }

          if (!leaseType || effectiveType === LeaseType.Soft) {
            if (releaseUserId) {
              unit.softLeases = unit.softLeases.filter(
                (existing) => existing.userId !== releaseUserId,
              );
            } else {
              unit.softLeases = [];
            }
          }
        }

        ensureSoftLeases(unit);
      };

      const collections = [
        state.storageUnits,
        state.templates,
        state.approvedFiles,
        state.archived,
      ];
      collections.forEach((collection) =>
        applyLeaseChanges(collection.find((u) => u.id === id)),
      );

      if (state.currentFile.id === id) {
        const currentExclusive = state.currentFile.exclusiveLease;
        const currentSoft = [...(state.currentFile.softLeases ?? [])];

        state.currentFile = {
          ...state.currentFile,
          exclusiveLease: (() => {
            if (
              type === LeaseAction.Acquire &&
              effectiveType === LeaseType.Exclusive
            ) {
              return leaseCopy;
            }
            if (type === LeaseAction.Release) {
              if (!leaseType || effectiveType === LeaseType.Exclusive) {
                if (
                  !releaseUserId ||
                  currentExclusive?.userId === releaseUserId
                ) {
                  return undefined;
                }
              }
            }
            return currentExclusive;
          })(),
          softLeases: (() => {
            if (
              type === LeaseAction.Acquire &&
              effectiveType === LeaseType.Soft &&
              leaseCopy
            ) {
              return [
                ...currentSoft.filter(
                  (existing) => existing.userId !== leaseCopy.userId,
                ),
                leaseCopy,
              ];
            }
            if (!leaseType || effectiveType === LeaseType.Soft) {
              if (type === LeaseAction.Release) {
                if (releaseUserId) {
                  return currentSoft.filter(
                    (existing) => existing.userId !== releaseUserId,
                  );
                }
                return [];
              }
            }
            if (
              type === LeaseAction.Acquire &&
              effectiveType === LeaseType.Exclusive
            ) {
              return [];
            }
            return currentSoft;
          })(),
        };
        state.currentFile = normalizeLeases(state.currentFile as StorageUnit);
      }
    });
  },
});

export const {
  addStorageUnit,
  getStorageUnitsResponse,
  createStorageUnitRequested,
  createStorageUnitSuccess,
  setExplorerLoading,
  removeStorageUnitSuccess,
  archiveStorageUnitSuccess,
  restoreStorageUnitSuccess,
  deleteAllArchviedSuccess,
  getAccessInformationForStorageUnitFromContextMenuSuccess,
  getAccessInformationForStorageUnitFromMembersSuccess,
  setSearchString,
  setSharedWithUsersLoading,
  grantAccessFromContextMenuSuccess,
  grantAccessFromMembersSuccess,
  clearAccessInformationForStorageUnit,
  removeAccessFromContextMenuSuccess,
  removeAccessFromMembersSuccess,
  createStorageUnitFailure,
  updateStorageUnitSuccess,
  updateStorageUnitLeases,
  clearMembers,
  setMembersLoading,
  setMoreDataToRetrieve,
  sortStorageUnits,
  setDirectAccess,
  setSort,
  setTemplates,
  setApprovedFiles,
  setRecentFiles,
  setRequestStatus,
  setFilters,
  setFilterSelection,
  setAdvancedFilters,
  setCurrentUploads,
  setCurrentFile,
  setCurrentFileName,
  addUpload,
  setArchived,
  generateMissingThumbnail,
} = storageUnitsSlice.actions;

export const storageUnitsReducer = storageUnitsSlice.reducer;
